import * as THREE from 'three'
import type {
  CapsuleCharacterMotorOptions,
  CapsuleCharacterMotorQueries,
  CharacterCommand,
  CharacterLocomotionState,
  CharacterSnapshot,
  CharacterStablePlacementOptions,
  CharacterStablePlacementResult,
} from '@/shared/types/character'
import type {
  SpatialCapsule,
  SpatialCollisionGroundHit,
  SpatialCollisionHit,
} from '@/shared/types/spatial'
import type { SpatialCollisionResolveCapsuleResult } from '@/shared/types/spatialCollisionResolve'

const UP = new THREE.Vector3(0, 0, 1)
const DOWN = new THREE.Vector3(0, 0, -1)
const EPSILON = 1e-5

const DEFAULT_STABLE_PLACEMENT_OPTIONS = {
  groundSearchUp: 0.5,
  groundSearchDown: 4,
  supportProbeRadiusScale: 0.8,
  supportProbeSamples: 8,
  settleSteps: 12,
  settleDelta: 1 / 60,
  maxSettlePlanarDisplacement: 0.03,
  maxSettleVerticalDisplacement: 0.05,
  maxSettleSpeed: 0.05,
} as const

type MovementResolution = {
  capsule: SpatialCapsule
  hit: SpatialCollisionHit | null
  grounded: boolean
  groundNormal: THREE.Vector3 | null
  collisionNormal: THREE.Vector3 | null
  appliedMotion: THREE.Vector3
}

type StepAssistCandidate = {
  capsule: SpatialCapsule
  groundNormal: THREE.Vector3 | null
  planarProgress: number
  stepHeight: number
}

type GroundAdjustment = {
  capsule: SpatialCapsule
  hit: SpatialCollisionGroundHit
  verticalOffset: number
}

type UnsupportedGroundProtectionResult = GroundAdjustment & {
  truncated: boolean
}

export type CapsuleCharacterMotorSnapshot = Omit<CharacterSnapshot, 'id'>

const DEFAULT_OPTIONS: Required<CapsuleCharacterMotorOptions> = {
  radius: 0.32,
  segmentLength: 1.08,
  eyeHeight: 1.55,
  groundControlMode: 'accelerated',
  groundContactGraceTime: 0,
  groundProbeRadiusScale: 0,
  groundProbeSamples: 0,
  moveSpeed: 4.8,
  runMultiplier: 1.55,
  jumpSpeed: 6.2,
  gravity: 20,
  groundAcceleration: 25,
  airAcceleration: 8,
  groundFriction: 10,
  groundStopSpeed: 0.05,
  maxStepHeight: 0.5,
  stepSearchSteps: 8,
  stepSearchIncrement: 0.08,
  stepSnapLockout: 0.08,
  groundSnapDistance: 0.18,
  groundFollowRise: 0.06,
  unsupportedGroundProtection: true,
  unsupportedGroundMaxDrop: 0.68,
  unsupportedGroundSearchSteps: 8,
  unsupportedGroundRefineIterations: 4,
  airSnapDistance: 0.06,
  airSnapMaxFallSpeed: 1.6,
  jumpSnapLockout: 0.14,
  maxSlopeAngleDeg: 50,
  collisionIterations: 2,
  depenetrationIterations: 2,
  maxSweepSteps: 8,
  maxSubsteps: 4,
  skinWidth: 0.02,
}

/** 通用胶囊角色运动核心。 */
export class CapsuleCharacterMotor {
  private readonly options: Required<CapsuleCharacterMotorOptions>
  private capsuleStart = new THREE.Vector3(0, 0, 1.2)
  private velocity = new THREE.Vector3()
  private grounded = false
  private groundNormal: THREE.Vector3 | null = null
  private stepHeight = 0
  private jumpSnapLockout = 0
  private groundSnapLockout = 0
  private groundContactGrace = 0
  private bodyYaw = 0
  private aimYaw = 0
  private aimPitch = 0
  private locomotionState: CharacterLocomotionState = 'idle'
  private collisionWaiting = false
  private _placementReady: boolean
  private pendingPlacement?: { point: THREE.Vector3; floor: boolean }

  constructor(
    private readonly queries: CapsuleCharacterMotorQueries,
    options: CapsuleCharacterMotorOptions = {}
  ) {
    this._placementReady = !queries.ensureCoverage
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
    }
  }

  get radius(): number {
    return this.options.radius
  }

  /** Resolves and verifies a stable floor point without requiring a persistent character. */
  resolveStablePlacement(
    options: Omit<CharacterStablePlacementOptions, 'spatialQuery' | 'motorOptions'>
  ): CharacterStablePlacementResult {
    const candidatePoint = options.candidatePoint
    if (![candidatePoint.x, candidatePoint.y, candidatePoint.z].every(Number.isFinite)) {
      return { stable: false, reason: 'ground-not-found' }
    }

    const groundSearchUp = Math.max(
      0,
      options.groundSearchUp ?? DEFAULT_STABLE_PLACEMENT_OPTIONS.groundSearchUp
    )
    const groundSearchDown = Math.max(
      0,
      options.groundSearchDown ?? DEFAULT_STABLE_PLACEMENT_OPTIONS.groundSearchDown
    )
    const searchBounds = new THREE.Box3(
      candidatePoint.clone(),
      candidatePoint.clone()
    ).expandByScalar(this.options.radius + this.options.skinWidth)
    searchBounds.min.z -= groundSearchDown
    searchBounds.max.z += groundSearchUp + this.options.segmentLength + this.options.radius * 2
    if (this.queries.ensureCoverage && !this.queries.ensureCoverage(searchBounds))
      return { stable: false, reason: 'collision-not-ready' }
    const groundHit = this.queries.resolveGround(
      candidatePoint.clone().addScaledVector(UP, groundSearchUp),
      DOWN,
      groundSearchUp + groundSearchDown
    )
    if (!groundHit) {
      return { stable: false, reason: 'ground-not-found' }
    }
    if (!this.isWalkable(groundHit)) {
      return { stable: false, reason: 'ground-too-steep' }
    }

    const floorPoint = groundHit.point.clone()
    const spawnCapsule = this.createSpawnCapsule(floorPoint)
    if (this.queries.overlapCapsule(spawnCapsule)) {
      return { stable: false, reason: 'capsule-overlap' }
    }
    if (!this.hasStableFootprintSupport(floorPoint, options)) {
      return { stable: false, reason: 'insufficient-support' }
    }

    this.setFloorPoint(floorPoint)
    const initialSnapshot = this.getSnapshot()
    let settledSnapshot = initialSnapshot
    const settleSteps = Math.max(
      1,
      Math.floor(options.settleSteps ?? DEFAULT_STABLE_PLACEMENT_OPTIONS.settleSteps)
    )
    const settleDelta = THREE.MathUtils.clamp(
      options.settleDelta ?? DEFAULT_STABLE_PLACEMENT_OPTIONS.settleDelta,
      EPSILON,
      0.05
    )
    const settleCommand: CharacterCommand = {
      move: { forward: 0, right: 0 },
      run: false,
      jump: false,
      bodyYaw: 0,
      aimYaw: 0,
      aimPitch: 0,
    }
    for (let index = 0; index < settleSteps; index += 1) {
      settledSnapshot = this.update(settleCommand, settleDelta)
    }

    const displacement = settledSnapshot.floorPoint.clone().sub(initialSnapshot.floorPoint)
    const planarDisplacement = Math.hypot(displacement.x, displacement.y)
    const maxPlanarDisplacement = Math.max(
      0,
      options.maxSettlePlanarDisplacement ??
        DEFAULT_STABLE_PLACEMENT_OPTIONS.maxSettlePlanarDisplacement
    )
    const maxVerticalDisplacement = Math.max(
      0,
      options.maxSettleVerticalDisplacement ??
        DEFAULT_STABLE_PLACEMENT_OPTIONS.maxSettleVerticalDisplacement
    )
    const maxSettleSpeed = Math.max(
      0,
      options.maxSettleSpeed ?? DEFAULT_STABLE_PLACEMENT_OPTIONS.maxSettleSpeed
    )
    if (
      !settledSnapshot.grounded ||
      planarDisplacement > maxPlanarDisplacement + EPSILON ||
      Math.abs(displacement.z) > maxVerticalDisplacement + EPSILON ||
      settledSnapshot.speed > maxSettleSpeed + EPSILON
    ) {
      return { stable: false, reason: 'unstable-after-settle' }
    }

    return {
      stable: true,
      floorPoint: settledSnapshot.floorPoint.clone().addScaledVector(UP, -this.options.skinWidth),
      snapshot: settledSnapshot,
    }
  }

  setCapsuleStart(point: THREE.Vector3): void {
    if (!this.checkPlacement(point, false)) return
    this._placementReady = true
    this.capsuleStart.copy(point)
    this.velocity.set(0, 0, 0)
    this.stepHeight = 0
    this.grounded = false
    this.groundNormal = null
    this.jumpSnapLockout = 0
    this.groundSnapLockout = 0
    this.groundContactGrace = 0
    this.locomotionState = 'idle'
  }

  setFloorPoint(point: THREE.Vector3): void {
    if (!this.checkPlacement(point, true)) return
    this._placementReady = true
    this.capsuleStart.copy(this.resolveSpawnCapsuleStart(point))
    this.velocity.set(0, 0, 0)
    this.grounded = true
    this.groundNormal = UP.clone()
    this.stepHeight = 0
    this.jumpSnapLockout = 0
    this.groundSnapLockout = 0
    this.groundContactGrace = this.options.groundContactGraceTime
    this.locomotionState = 'idle'
  }

  setOrientation(bodyYaw: number, aimYaw: number, aimPitch: number): void {
    this.bodyYaw = bodyYaw
    this.aimYaw = aimYaw
    this.aimPitch = aimPitch
  }

  getSnapshot(): CapsuleCharacterMotorSnapshot {
    const capsule = this.createCapsule()
    const planarSpeed = Math.hypot(this.velocity.x, this.velocity.y)

    return {
      placementPending: !this._placementReady,
      collisionWaiting: this.collisionWaiting,
      capsule,
      floorPoint: capsule.start.clone().addScaledVector(UP, -capsule.radius),
      eyePosition: capsule.start
        .clone()
        .addScaledVector(UP, this.options.eyeHeight - this.options.radius),
      velocity: this.velocity.clone(),
      grounded: this.grounded,
      groundNormal: this.groundNormal?.clone() ?? null,
      stepHeight: this.stepHeight,
      speed: this.velocity.length(),
      planarSpeed,
      bodyYaw: this.bodyYaw,
      aimYaw: this.aimYaw,
      aimPitch: this.aimPitch,
      locomotionState: this.locomotionState,
    }
  }

  update(command: CharacterCommand, delta: number): CapsuleCharacterMotorSnapshot {
    if (this.pendingPlacement) {
      const { point, floor } = this.pendingPlacement
      if (floor) this.setFloorPoint(point)
      else this.setCapsuleStart(point)
      if (this.pendingPlacement) return this.getSnapshot()
    }
    if (this.queries.ensureCoverage) {
      const capsule = this.createCapsule()
      const motion =
        Math.max(
          this.velocity.length(),
          this.options.moveSpeed * this.options.runMultiplier,
          this.options.jumpSpeed
        ) *
          Math.max(0, Math.min(delta, 0.05)) +
        this.options.gravity * Math.max(0, Math.min(delta, 0.05)) ** 2
      const bounds = new THREE.Box3()
        .setFromPoints([capsule.start, capsule.end])
        .expandByScalar(capsule.radius + motion + this.options.skinWidth)
      bounds.min.z -= Math.max(
        this.options.unsupportedGroundMaxDrop,
        this.options.groundSnapDistance
      )
      bounds.max.z += this.options.maxStepHeight
      this.collisionWaiting = !this.queries.ensureCoverage(bounds)
      if (this.collisionWaiting) {
        this.velocity.set(0, 0, 0)
        return this.getSnapshot()
      }
      this._placementReady = true
    }
    const clampedDelta = Math.min(delta, 0.05)
    const substeps = Math.max(
      1,
      Math.min(this.options.maxSubsteps, Math.ceil(clampedDelta / (1 / 60)))
    )
    const stepDelta = clampedDelta / substeps
    let jumpRequested = command.jump
    let landed = false

    this.setOrientation(command.bodyYaw, command.aimYaw, command.aimPitch)

    for (let index = 0; index < substeps; index += 1) {
      landed = this.step(command, stepDelta, jumpRequested) || landed
      jumpRequested = false
    }

    this.locomotionState = this.resolveLocomotionState(command, landed)
    return this.getSnapshot()
  }

  private step(command: CharacterCommand, delta: number, jumpRequested: boolean): boolean {
    this.stepHeight = 0
    const wasGrounded = this.grounded

    const groundVelocitySolved = this.applyControlVelocity(command, delta, wasGrounded)
    if (jumpRequested && wasGrounded) {
      this.velocity.z = this.options.jumpSpeed
      this.grounded = false
      this.groundNormal = null
      this.jumpSnapLockout = this.options.jumpSnapLockout
      this.groundSnapLockout = 0
      this.groundContactGrace = 0
    }

    let damping = groundVelocitySolved ? 0 : Math.exp(-4 * delta) - 1
    if (!this.grounded) {
      this.velocity.addScaledVector(DOWN, this.options.gravity * delta)
      damping *= 0.1
    }

    this.velocity.addScaledVector(this.velocity, damping)
    if (wasGrounded) {
      const planarSpeedSq = this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y
      if (planarSpeedSq <= this.options.groundStopSpeed ** 2) {
        this.velocity.x = 0
        this.velocity.y = 0
      }
    }

    const startCapsule = this.createCapsule()
    const preCollisionVelocityZ = this.velocity.z
    const motion = this.velocity.clone().multiplyScalar(delta)
    const planarMotion = new THREE.Vector3(motion.x, motion.y, 0)
    const movingPlanar = planarMotion.lengthSq() > EPSILON * EPSILON
    const shouldProtectUnsupportedGround = this.shouldProtectUnsupportedGround(
      wasGrounded,
      jumpRequested,
      movingPlanar
    )
    let movement = this.moveAndCollide(startCapsule, motion, motion.z < 0 && !movingPlanar)
    let preservedGroundContact = false
    let unsupportedGroundBlocked = false

    if (wasGrounded && movingPlanar) {
      const currentProgress = getPlanarProgress(startCapsule, movement.capsule, planarMotion)
      const blockedNormal = movement.collisionNormal
      const blockedByWall =
        !!movement.hit &&
        currentProgress < planarMotion.length() * 0.9 &&
        // 三角网格台阶的胶囊接触法线可能偏向上方，仍应尝试有界抬升。
        (!blockedNormal || blockedNormal.dot(UP) < 1 - EPSILON)
      if (blockedByWall) {
        const stepCandidate = this.tryStepAssist(startCapsule, planarMotion, currentProgress)
        if (stepCandidate && stepCandidate.planarProgress > currentProgress + EPSILON) {
          movement = {
            capsule: stepCandidate.capsule,
            hit: movement.hit,
            grounded: true,
            groundNormal: stepCandidate.groundNormal?.clone() ?? UP.clone(),
            collisionNormal: null,
            appliedMotion: stepCandidate.capsule.start.clone().sub(startCapsule.start),
          }
          this.stepHeight = stepCandidate.stepHeight
          this.groundSnapLockout = Math.max(this.groundSnapLockout, this.options.stepSnapLockout)
        }
      }
    }

    if (!movement.grounded) {
      const groundAdjustment = this.followGround(
        movement.capsule,
        wasGrounded,
        movingPlanar,
        shouldProtectUnsupportedGround ? this.options.unsupportedGroundMaxDrop : null
      )
      if (groundAdjustment) {
        movement = {
          capsule: groundAdjustment.capsule,
          hit: movement.hit,
          grounded: true,
          groundNormal: groundAdjustment.hit.normal?.clone() ?? UP.clone(),
          collisionNormal: movement.collisionNormal,
          appliedMotion: groundAdjustment.capsule.start.clone().sub(startCapsule.start),
        }
      }
    }

    if (!movement.grounded && shouldProtectUnsupportedGround) {
      const protectedGround = this.resolveUnsupportedGroundProtection(
        startCapsule,
        movement.capsule,
        planarMotion
      )
      if (protectedGround) {
        unsupportedGroundBlocked = protectedGround.truncated
        movement = {
          capsule: protectedGround.capsule,
          hit: movement.hit ?? protectedGround.hit,
          grounded: true,
          groundNormal: protectedGround.hit.normal?.clone() ?? UP.clone(),
          collisionNormal: movement.collisionNormal,
          appliedMotion: protectedGround.capsule.start.clone().sub(startCapsule.start),
        }
      }
    }

    if (!movement.grounded) {
      if (wasGrounded && movingPlanar && this.groundSnapLockout > 0 && this.velocity.z <= EPSILON) {
        movement = {
          ...movement,
          grounded: true,
          groundNormal: this.groundNormal?.clone() ?? UP.clone(),
        }
      } else if (this.canPreserveGroundContact(wasGrounded)) {
        preservedGroundContact = true
        movement = {
          ...movement,
          grounded: true,
          groundNormal: this.groundNormal?.clone() ?? UP.clone(),
        }
      }
    }

    const landed =
      !preservedGroundContact &&
      !wasGrounded &&
      movement.grounded &&
      preCollisionVelocityZ < -EPSILON

    this.applyCapsule(movement.capsule)
    this.grounded = movement.grounded
    this.groundNormal = movement.groundNormal

    if (movement.grounded) {
      this.groundContactGrace = preservedGroundContact
        ? Math.max(0, this.groundContactGrace - delta)
        : this.options.groundContactGraceTime
      if (unsupportedGroundBlocked) {
        this.clearUnsupportedGroundVelocity(planarMotion)
      }
      if (this.velocity.z < 0) {
        this.velocity.z = 0
      }
    } else {
      this.groundContactGrace = 0
      if (movement.collisionNormal) {
        const into = this.velocity.dot(movement.collisionNormal)
        if (into < 0) {
          this.velocity.addScaledVector(movement.collisionNormal, -into)
        }
      }
    }

    this.jumpSnapLockout = Math.max(0, this.jumpSnapLockout - delta)
    this.groundSnapLockout = Math.max(0, this.groundSnapLockout - delta)
    return landed
  }

  private checkPlacement(point: THREE.Vector3, floor: boolean): boolean {
    if (!this.queries.ensureCoverage) return true
    const start = floor ? this.resolveSpawnCapsuleStart(point) : point
    const bounds = new THREE.Box3()
      .setFromPoints([start, start.clone().addScaledVector(UP, this.options.segmentLength)])
      .expandByScalar(this.options.radius + this.options.skinWidth)
    bounds.min.z -= this.options.unsupportedGroundMaxDrop
    this.collisionWaiting = !this.queries.ensureCoverage(bounds)
    this.pendingPlacement = this.collisionWaiting ? { point: point.clone(), floor } : undefined
    return !this.collisionWaiting
  }

  private applyControlVelocity(
    command: CharacterCommand,
    delta: number,
    wasGrounded: boolean
  ): boolean {
    const wishDirection = getWishDirection(command.bodyYaw, command.move)
    if (wasGrounded && this.options.groundControlMode === 'directional') {
      this.applyDirectionalGroundVelocity(wishDirection, command, delta)
      return true
    }

    if (wishDirection.lengthSq() <= EPSILON) {
      return false
    }

    const speedScale = command.run ? this.options.runMultiplier : 1
    const acceleration =
      (wasGrounded ? this.options.groundAcceleration : this.options.airAcceleration) * speedScale
    this.velocity.addScaledVector(wishDirection, acceleration * delta)

    const maxPlanarSpeed = this.options.moveSpeed * speedScale
    const planarSpeed = Math.hypot(this.velocity.x, this.velocity.y)
    if (planarSpeed > maxPlanarSpeed && planarSpeed > EPSILON) {
      const scale = maxPlanarSpeed / planarSpeed
      this.velocity.x *= scale
      this.velocity.y *= scale
    }

    return false
  }

  private applyDirectionalGroundVelocity(
    wishDirection: THREE.Vector3,
    command: CharacterCommand,
    delta: number
  ): void {
    const currentPlanarVelocity = new THREE.Vector3(this.velocity.x, this.velocity.y, 0)
    const currentSpeed = currentPlanarVelocity.length()
    const hasMovement = wishDirection.lengthSq() > EPSILON
    const speedScale = command.run ? this.options.runMultiplier : 1
    const targetSpeed = hasMovement ? this.options.moveSpeed * speedScale : 0
    const responseRate =
      targetSpeed > currentSpeed ? this.options.groundAcceleration : this.options.groundFriction
    const alpha = 1 - Math.exp(-Math.max(responseRate, EPSILON) * delta)
    let nextSpeed = THREE.MathUtils.lerp(currentSpeed, targetSpeed, alpha)
    if (Math.abs(nextSpeed - targetSpeed) <= this.options.groundStopSpeed) {
      nextSpeed = targetSpeed
    }

    if (nextSpeed <= this.options.groundStopSpeed && !hasMovement) {
      this.velocity.x = 0
      this.velocity.y = 0
      return
    }

    const direction = hasMovement
      ? wishDirection
      : currentPlanarVelocity.lengthSq() > EPSILON
        ? currentPlanarVelocity.normalize()
        : null
    if (!direction) {
      this.velocity.x = 0
      this.velocity.y = 0
      return
    }

    this.velocity.x = direction.x * nextSpeed
    this.velocity.y = direction.y * nextSpeed
  }

  private canPreserveGroundContact(wasGrounded: boolean): boolean {
    return (
      this.options.groundContactGraceTime > 0 &&
      wasGrounded &&
      this.groundContactGrace > 0 &&
      this.jumpSnapLockout <= 0 &&
      this.velocity.z <= EPSILON
    )
  }

  private shouldProtectUnsupportedGround(
    wasGrounded: boolean,
    jumpRequested: boolean,
    movingPlanar: boolean
  ): boolean {
    return (
      this.options.unsupportedGroundProtection &&
      wasGrounded &&
      !jumpRequested &&
      movingPlanar &&
      this.jumpSnapLockout <= 0
    )
  }

  private resolveUnsupportedGroundProtection(
    startCapsule: SpatialCapsule,
    targetCapsule: SpatialCapsule,
    planarMotion: THREE.Vector3
  ): UnsupportedGroundProtectionResult | null {
    const targetSupport = this.resolveGroundAdjustment(
      targetCapsule,
      this.options.groundFollowRise,
      Math.max(0, this.options.unsupportedGroundMaxDrop)
    )
    if (targetSupport) {
      return {
        ...targetSupport,
        truncated: false,
      }
    }

    const startSupport = this.resolveGroundAdjustment(
      startCapsule,
      this.options.groundFollowRise,
      0
    )
    if (!startSupport) {
      return null
    }

    let bestSupport = startSupport
    let bestFraction = 0
    let firstUnsupportedFraction: number | null = null
    const searchSteps = Math.max(1, Math.floor(this.options.unsupportedGroundSearchSteps))

    for (let stepIndex = 1; stepIndex <= searchSteps; stepIndex += 1) {
      const fraction = stepIndex / searchSteps
      const support = this.resolveUnsupportedGroundAtFraction(startCapsule, planarMotion, fraction)
      if (!support) {
        firstUnsupportedFraction = fraction
        break
      }

      bestSupport = support
      bestFraction = fraction
    }

    if (firstUnsupportedFraction === null) {
      return {
        ...bestSupport,
        truncated: bestFraction < 1 - EPSILON,
      }
    }

    const refineIterations = Math.max(0, Math.floor(this.options.unsupportedGroundRefineIterations))
    let unsupportedFraction = firstUnsupportedFraction
    for (let iteration = 0; iteration < refineIterations; iteration += 1) {
      const fraction = (bestFraction + unsupportedFraction) * 0.5
      const support = this.resolveUnsupportedGroundAtFraction(startCapsule, planarMotion, fraction)
      if (support) {
        bestSupport = support
        bestFraction = fraction
      } else {
        unsupportedFraction = fraction
      }
    }

    return {
      ...bestSupport,
      truncated: true,
    }
  }

  private resolveUnsupportedGroundAtFraction(
    startCapsule: SpatialCapsule,
    planarMotion: THREE.Vector3,
    fraction: number
  ): GroundAdjustment | null {
    const candidate = cloneCapsule(startCapsule)
    translateCapsule(candidate, planarMotion.clone().multiplyScalar(fraction))
    return this.resolveGroundAdjustment(
      candidate,
      this.options.groundFollowRise,
      Math.max(0, this.options.unsupportedGroundMaxDrop)
    )
  }

  private clearUnsupportedGroundVelocity(planarMotion: THREE.Vector3): void {
    const direction = new THREE.Vector3(planarMotion.x, planarMotion.y, 0)
    const distance = direction.length()
    if (distance <= EPSILON) {
      return
    }

    direction.divideScalar(distance)
    const intoUnsupportedGround = this.velocity.x * direction.x + this.velocity.y * direction.y
    if (intoUnsupportedGround <= EPSILON) {
      return
    }

    this.velocity.x -= direction.x * intoUnsupportedGround
    this.velocity.y -= direction.y * intoUnsupportedGround
    if (Math.abs(this.velocity.x) <= EPSILON) {
      this.velocity.x = 0
    }
    if (Math.abs(this.velocity.y) <= EPSILON) {
      this.velocity.y = 0
    }
  }

  private moveAndCollide(
    startCapsule: SpatialCapsule,
    motion: THREE.Vector3,
    preserveWalkableVerticalStop = false
  ): MovementResolution {
    if (motion.lengthSq() <= EPSILON * EPSILON) {
      return {
        capsule: cloneCapsule(startCapsule),
        hit: null,
        grounded: false,
        groundNormal: null,
        collisionNormal: null,
        appliedMotion: new THREE.Vector3(),
      }
    }

    if (!this.queries.resolveCapsule) {
      return this.moveWithSlide(startCapsule, motion, preserveWalkableVerticalStop)
    }

    const movedCapsule = cloneCapsule(startCapsule)
    translateCapsule(movedCapsule, motion)
    const hit = this.resolveCapsulePenetration(
      movedCapsule,
      this.options.collisionIterations + this.options.depenetrationIterations
    )
    const collisionNormal = hit ? normalizeCollisionNormal(hit) : null
    const grounded = !!(hit && collisionNormal && this.isWalkable(hit))

    return {
      capsule: movedCapsule,
      hit,
      grounded,
      groundNormal: grounded ? collisionNormal.clone() : null,
      collisionNormal,
      appliedMotion: movedCapsule.start.clone().sub(startCapsule.start),
    }
  }

  private tryStepAssist(
    capsule: SpatialCapsule,
    motion: THREE.Vector3,
    minimumProgress: number
  ): StepAssistCandidate | null {
    if (motion.lengthSq() <= EPSILON * EPSILON) {
      return null
    }

    let bestCandidate: StepAssistCandidate | null = null
    let bestProgress = minimumProgress
    const probeHeights = this.buildStepAssistProbeHeights()
    for (const liftHeight of probeHeights) {
      const candidate = this.tryStepAssistHeight(capsule, motion, liftHeight)
      if (!candidate) {
        continue
      }

      if (candidate.planarProgress + EPSILON < bestProgress) {
        continue
      }

      const shouldReplace =
        !bestCandidate ||
        candidate.planarProgress > bestProgress + EPSILON ||
        candidate.stepHeight < bestCandidate.stepHeight
      if (!shouldReplace) {
        continue
      }

      bestCandidate = candidate
      bestProgress = candidate.planarProgress
      if (candidate.planarProgress >= motion.length() * 0.95) {
        break
      }
    }

    return bestCandidate
  }

  private buildStepAssistProbeHeights(): number[] {
    const maxStepHeight = Math.max(this.options.maxStepHeight, this.options.skinWidth)
    const increment = Math.max(this.options.stepSearchIncrement, this.options.skinWidth * 2)
    const denseCeiling = Math.min(
      maxStepHeight,
      Math.max(0.8, increment * this.options.stepSearchSteps)
    )
    const heights: number[] = []

    for (let liftHeight = increment; liftHeight < denseCeiling - EPSILON; liftHeight += increment) {
      pushStepProbeHeight(heights, liftHeight)
    }
    pushStepProbeHeight(heights, denseCeiling)

    if (maxStepHeight <= denseCeiling + EPSILON) {
      return heights
    }

    const coarseSamples = Math.max(1, this.options.stepSearchSteps)
    for (let sampleIndex = 1; sampleIndex <= coarseSamples; sampleIndex += 1) {
      const t = sampleIndex / coarseSamples
      pushStepProbeHeight(heights, THREE.MathUtils.lerp(denseCeiling, maxStepHeight, t))
    }

    return heights
  }

  private tryStepAssistHeight(
    capsule: SpatialCapsule,
    motion: THREE.Vector3,
    liftHeight: number
  ): StepAssistCandidate | null {
    const liftedCapsule = cloneCapsule(capsule)
    translateCapsule(liftedCapsule, UP.clone().multiplyScalar(liftHeight + this.options.skinWidth))

    const liftedHit = this.resolveCapsulePenetration(
      liftedCapsule,
      this.options.depenetrationIterations
    )
    if (liftedHit && Math.abs(liftedCapsule.start.z - capsule.start.z) <= EPSILON) {
      return null
    }
    if (this.queries.overlapCapsule(liftedCapsule)) {
      return null
    }

    const movement = this.moveAndCollide(liftedCapsule, motion)
    let steppedCapsule = movement.capsule
    let groundNormal = movement.groundNormal

    if (!movement.grounded) {
      const groundAdjustment = this.resolveGroundAdjustment(
        movement.capsule,
        this.options.skinWidth,
        liftHeight + this.options.groundSnapDistance,
        true
      )
      if (!groundAdjustment) {
        return null
      }

      steppedCapsule = groundAdjustment.capsule
      groundNormal = groundAdjustment.hit.normal?.clone() ?? UP.clone()
    }

    const steppedHeight = steppedCapsule.start.z - capsule.start.z
    if (
      steppedHeight <= EPSILON ||
      steppedHeight > this.options.maxStepHeight + this.options.skinWidth
    ) {
      return null
    }

    return {
      capsule: steppedCapsule,
      groundNormal,
      planarProgress: getPlanarProgress(capsule, steppedCapsule, motion),
      stepHeight: steppedHeight,
    }
  }

  private moveWithSlide(
    startCapsule: SpatialCapsule,
    motion: THREE.Vector3,
    preserveWalkableVerticalStop: boolean
  ): MovementResolution {
    const nextCapsule = cloneCapsule(startCapsule)
    const initialStart = nextCapsule.start.clone()
    this.depenetrateCapsule(nextCapsule)

    const remaining = motion.clone()
    let lastHit: SpatialCollisionHit | null = null
    let collisionNormal: THREE.Vector3 | null = null
    let grounded = false
    let groundNormal: THREE.Vector3 | null = null

    for (let iteration = 0; iteration < this.options.collisionIterations; iteration += 1) {
      if (remaining.lengthSq() <= EPSILON * EPSILON) {
        break
      }

      const hit = this.queries.sweepCapsule(nextCapsule, remaining, this.options.maxSweepSteps)
      if (!hit) {
        translateCapsule(nextCapsule, remaining)
        remaining.set(0, 0, 0)
        break
      }

      lastHit = hit
      collisionNormal = normalizeCollisionNormal(hit)
      const direction = remaining.clone().normalize()
      const rawTravelDistance = Math.max(hit.travelDistance ?? 0, 0)
      const safeTravelDistance = Math.max(rawTravelDistance - this.options.skinWidth, 0)

      if (safeTravelDistance > EPSILON) {
        translateCapsule(nextCapsule, direction.clone().multiplyScalar(safeTravelDistance))
      }

      const resolvedHit = this.resolveCapsulePenetration(
        nextCapsule,
        this.options.depenetrationIterations
      )
      if (resolvedHit) {
        lastHit = resolvedHit
        collisionNormal = normalizeCollisionNormal(resolvedHit)
      }

      if (
        preserveWalkableVerticalStop &&
        remaining.z < 0 &&
        Math.abs(remaining.x) <= EPSILON &&
        Math.abs(remaining.y) <= EPSILON &&
        lastHit &&
        this.isWalkable(lastHit)
      ) {
        grounded = true
        groundNormal = normalizeCollisionNormal(lastHit)
        remaining.set(0, 0, 0)
        break
      }

      remaining.sub(direction.multiplyScalar(rawTravelDistance))
      if (!collisionNormal) {
        remaining.set(0, 0, 0)
        break
      }

      if (lastHit && this.isWalkable(lastHit) && remaining.z <= EPSILON) {
        grounded = true
        groundNormal = collisionNormal.clone()
      }

      const into = remaining.dot(collisionNormal)
      if (into < 0) {
        remaining.addScaledVector(collisionNormal, -into)
      }
    }

    return {
      capsule: nextCapsule,
      hit: lastHit,
      grounded,
      groundNormal,
      collisionNormal,
      appliedMotion: nextCapsule.start.clone().sub(initialStart),
    }
  }

  private followGround(
    capsule: SpatialCapsule,
    wasGrounded: boolean,
    movingPlanar: boolean,
    groundedMoveMaxDrop: number | null = null
  ): GroundAdjustment | null {
    if (this.jumpSnapLockout > 0) {
      return null
    }

    if (!wasGrounded) {
      if (this.velocity.z < -this.options.airSnapMaxFallSpeed) {
        return null
      }

      return this.resolveGroundAdjustment(capsule, 0.02, this.options.airSnapDistance)
    }

    if (!movingPlanar) {
      return this.resolveGroundAdjustment(capsule, this.options.groundFollowRise, 0)
    }

    if (this.groundSnapLockout > 0) {
      return this.resolveGroundAdjustment(capsule, this.options.groundFollowRise, 0)
    }

    const maxDrop =
      groundedMoveMaxDrop ?? this.options.maxStepHeight + this.options.groundSnapDistance
    return this.resolveGroundAdjustment(
      capsule,
      this.options.groundFollowRise,
      Math.max(0, maxDrop),
      true
    )
  }

  private resolveGroundAdjustment(
    capsule: SpatialCapsule,
    maxRise: number,
    maxDrop: number,
    preferHigher = false
  ): GroundAdjustment | null {
    const groundProbe = this.resolveGroundProbe(capsule, maxRise, maxDrop, preferHigher)
    if (!groundProbe) {
      return null
    }

    const adjustedCapsule = cloneCapsule(capsule)
    translateCapsule(adjustedCapsule, UP.clone().multiplyScalar(groundProbe.verticalOffset))
    this.depenetrateCapsule(adjustedCapsule)

    return {
      capsule: adjustedCapsule,
      hit: groundProbe.hit,
      verticalOffset: groundProbe.verticalOffset,
    }
  }

  private resolveGroundProbe(
    capsule: SpatialCapsule,
    maxRise: number,
    maxDrop: number,
    preferHigher = false
  ): { hit: SpatialCollisionGroundHit; verticalOffset: number } | null {
    const centerProbe = this.resolveGroundProbeAtOffset(
      capsule,
      new THREE.Vector3(),
      maxRise,
      maxDrop
    )
    if (centerProbe && !preferHigher) {
      return centerProbe
    }

    let bestProbe: {
      hit: SpatialCollisionGroundHit
      verticalOffset: number
    } | null = centerProbe
    for (const offset of this.createGroundProbeOffsets(preferHigher ? this.options.skinWidth : 0)) {
      const probe = this.resolveGroundProbeAtOffset(capsule, offset, maxRise, maxDrop)
      if (!probe) {
        continue
      }

      // 跨台阶时中心仍在旧地面，不能让它遮蔽胶囊前缘已覆盖的新支撑。
      if (
        !bestProbe ||
        (preferHigher
          ? probe.verticalOffset > bestProbe.verticalOffset
          : Math.abs(probe.verticalOffset) < Math.abs(bestProbe.verticalOffset))
      ) {
        bestProbe = probe
      }
    }

    return bestProbe
  }

  private resolveGroundProbeAtOffset(
    capsule: SpatialCapsule,
    offset: THREE.Vector3,
    maxRise: number,
    maxDrop: number
  ): { hit: SpatialCollisionGroundHit; verticalOffset: number } | null {
    const probeOrigin = capsule.start
      .clone()
      .add(offset)
      .addScaledVector(UP, maxRise + this.options.radius)
    const hit = this.queries.resolveGround(
      probeOrigin,
      DOWN,
      maxRise + maxDrop + this.options.radius * 2 + this.options.skinWidth + EPSILON
    )
    if (!hit || !this.isWalkable(hit)) {
      return null
    }

    const desiredStart = hit.point
      .clone()
      .addScaledVector(UP, this.options.radius + this.options.skinWidth)
    const verticalOffset = desiredStart.sub(capsule.start).dot(UP)
    if (verticalOffset > maxRise + EPSILON || verticalOffset < -maxDrop - EPSILON) {
      return null
    }

    return {
      hit,
      verticalOffset,
    }
  }

  private createGroundProbeOffsets(extraRadius = 0): THREE.Vector3[] {
    const probeRadius =
      (this.options.radius + extraRadius) *
      THREE.MathUtils.clamp(this.options.groundProbeRadiusScale, 0, 1)
    const sampleCount = Math.max(0, Math.floor(this.options.groundProbeSamples))
    if (probeRadius <= EPSILON || sampleCount <= 0) {
      return []
    }

    const offsets: THREE.Vector3[] = []
    for (let index = 0; index < sampleCount; index += 1) {
      const angle = (index / sampleCount) * Math.PI * 2
      offsets.push(
        new THREE.Vector3(Math.cos(angle) * probeRadius, Math.sin(angle) * probeRadius, 0)
      )
    }

    return offsets
  }

  private hasStableFootprintSupport(
    floorPoint: THREE.Vector3,
    options: Omit<CharacterStablePlacementOptions, 'spatialQuery' | 'motorOptions'>
  ): boolean {
    const radiusScale = THREE.MathUtils.clamp(
      options.supportProbeRadiusScale ?? DEFAULT_STABLE_PLACEMENT_OPTIONS.supportProbeRadiusScale,
      0,
      1
    )
    const sampleCount = Math.max(
      0,
      Math.floor(
        options.supportProbeSamples ?? DEFAULT_STABLE_PLACEMENT_OPTIONS.supportProbeSamples
      )
    )
    if (radiusScale <= EPSILON || sampleCount === 0) {
      return true
    }

    const probeRadius = this.options.radius * radiusScale
    const maxSlopeRadians = THREE.MathUtils.degToRad(this.options.maxSlopeAngleDeg)
    const heightTolerance =
      probeRadius * Math.tan(maxSlopeRadians) + this.options.skinWidth * 2 + EPSILON
    for (let index = 0; index < sampleCount; index += 1) {
      const angle = (index / sampleCount) * Math.PI * 2
      const probeOrigin = floorPoint
        .clone()
        .add(
          new THREE.Vector3(
            Math.cos(angle) * probeRadius,
            Math.sin(angle) * probeRadius,
            heightTolerance
          )
        )
      const hit = this.queries.resolveGround(probeOrigin, DOWN, heightTolerance * 2)
      if (
        !hit ||
        !this.isWalkable(hit) ||
        Math.abs(hit.point.z - floorPoint.z) > heightTolerance + EPSILON
      ) {
        return false
      }
    }
    return true
  }

  private depenetrateCapsule(capsule: SpatialCapsule): void {
    this.resolveCapsulePenetration(capsule, this.options.depenetrationIterations)
  }

  private resolveCapsulePenetration(
    capsule: SpatialCapsule,
    maxIterations: number
  ): SpatialCollisionHit | SpatialCollisionResolveCapsuleResult | null {
    const resolved = this.queries.resolveCapsule?.(capsule, maxIterations, this.options.skinWidth)
    if (resolved) {
      capsule.start.copy(resolved.resolvedCapsule.start)
      capsule.end.copy(resolved.resolvedCapsule.end)
      return resolved
    }

    let lastHit: SpatialCollisionHit | null = null
    for (let iteration = 0; iteration < maxIterations; iteration += 1) {
      const hit = this.queries.overlapCapsule(capsule)
      if (!hit) {
        break
      }

      lastHit = hit
      const normal = normalizeCollisionNormal(hit)
      const penetrationDepth = Math.max(hit.penetrationDepth ?? 0, 0)
      if (!normal || penetrationDepth <= EPSILON) {
        break
      }

      translateCapsule(capsule, normal.multiplyScalar(penetrationDepth + this.options.skinWidth))
    }

    return lastHit
  }

  private isWalkable(hit: SpatialCollisionGroundHit | SpatialCollisionHit): boolean {
    const normal = normalizeCollisionNormal(hit)
    if (!normal) {
      return true
    }

    const maxSlopeCos = Math.cos(THREE.MathUtils.degToRad(this.options.maxSlopeAngleDeg))
    return normal.dot(UP) >= maxSlopeCos
  }

  private applyCapsule(capsule: SpatialCapsule): void {
    this.capsuleStart.copy(capsule.start)
  }

  private resolveSpawnCapsuleStart(point: THREE.Vector3): THREE.Vector3 {
    const capsule = this.createSpawnCapsule(point)
    const liftStep = Math.max(this.options.skinWidth * 2, this.options.radius * 0.5)
    const maxLift = Math.max(64, liftStep)
    const maxAttempts = Math.max(1, Math.ceil(maxLift / liftStep))

    for (let attempt = 0; attempt <= maxAttempts; attempt += 1) {
      if (!this.queries.overlapCapsule(capsule)) {
        return capsule.start.clone()
      }

      translateCapsule(capsule, UP.clone().multiplyScalar(liftStep))
      this.resolveCapsulePenetration(capsule, this.options.depenetrationIterations)
    }

    return capsule.start.clone()
  }

  private createSpawnCapsule(point: THREE.Vector3): SpatialCapsule {
    return {
      start: point.clone().addScaledVector(UP, this.options.radius + this.options.skinWidth),
      end: point
        .clone()
        .addScaledVector(
          UP,
          this.options.radius + this.options.skinWidth + this.options.segmentLength
        ),
      radius: this.options.radius,
    }
  }

  private createCapsule(): SpatialCapsule {
    return {
      start: this.capsuleStart.clone(),
      end: this.capsuleStart.clone().addScaledVector(UP, this.options.segmentLength),
      radius: this.options.radius,
    }
  }

  private resolveLocomotionState(
    command: CharacterCommand,
    landed: boolean
  ): CharacterLocomotionState {
    if (landed) {
      return 'land'
    }

    if (!this.grounded) {
      return this.velocity.z > EPSILON ? 'jump' : 'fall'
    }

    const planarSpeed = Math.hypot(this.velocity.x, this.velocity.y)
    if (planarSpeed <= Math.max(this.options.groundStopSpeed, EPSILON)) {
      return 'idle'
    }

    return command.run ? 'run' : 'walk'
  }
}

function getWishDirection(bodyYaw: number, move: CharacterCommand['move']): THREE.Vector3 {
  const forward = new THREE.Vector3(Math.cos(bodyYaw), Math.sin(bodyYaw), 0)
  const right = new THREE.Vector3(forward.y, -forward.x, 0)
  const direction = new THREE.Vector3()

  if (move.forward > 0) {
    direction.addScaledVector(forward, move.forward)
  }
  if (move.forward < 0) {
    direction.sub(forward.clone().multiplyScalar(-move.forward))
  }
  if (move.right > 0) {
    direction.addScaledVector(right, move.right)
  }
  if (move.right < 0) {
    direction.sub(right.clone().multiplyScalar(-move.right))
  }

  if (direction.lengthSq() > EPSILON) {
    direction.normalize()
  }

  return direction
}

function cloneCapsule(capsule: SpatialCapsule): SpatialCapsule {
  return {
    start: capsule.start.clone(),
    end: capsule.end.clone(),
    radius: capsule.radius,
  }
}

function translateCapsule(capsule: SpatialCapsule, motion: THREE.Vector3): void {
  capsule.start.add(motion)
  capsule.end.add(motion)
}

function normalizeCollisionNormal(
  hit: SpatialCollisionHit | SpatialCollisionGroundHit
): THREE.Vector3 | null {
  const source = hit.separationNormal ?? hit.normal
  if (!source || source.lengthSq() <= EPSILON) {
    return null
  }

  return source.clone().normalize()
}

function getPlanarProgress(
  from: SpatialCapsule,
  to: SpatialCapsule,
  desiredMotion: THREE.Vector3
): number {
  const direction = new THREE.Vector3(desiredMotion.x, desiredMotion.y, 0)
  const distance = direction.length()
  if (distance <= EPSILON) {
    return 0
  }

  direction.divideScalar(distance)
  const delta = to.start.clone().sub(from.start)
  delta.z = 0
  return Math.max(0, delta.dot(direction))
}

function pushStepProbeHeight(heights: number[], value: number): void {
  if (heights.length > 0 && Math.abs(heights[heights.length - 1] - value) <= EPSILON) {
    return
  }

  heights.push(value)
}
